<template>
  <error-boundary error-key="restrictionsCheckInOutSettings">
    <div class="d-flex justify-space-between">
      <create-restriction-button
        :operation="checkInOutRestrictions?.operations.add"
        @click="setIsRestrictionDialogOpen(true)"
      />
      <v-spacer />
      <restrictions-table-toggle-button
        :active="isRestrictionsTableOpen"
        @click="setIsRestrictionsTableOpen(!isRestrictionsTableOpen)"
      />
    </div>

    <div class="d-lg-flex flex-lg-row-reverse mt-md-6">
      <restrictions-table
        v-model:open="isRestrictionsTableOpen"
        :period="checkInOutRestrictionsChessPeriod"
        :table-headers="restrictionsTableHeaders"
        :table-items="restrictionsTableItems"
        :operation-delete="checkInOutRestrictions?.operations.remove"
        @show="handleRestrictionShow"
        @edit="handleRestrictionEdit"
        @remove="handleRestrictionRemove"
        @open:chess="setIsChessCalendarDialogOpen(true)"
      >
        <template v-slot:[`item.number`]="{ item }"> #{{ item.number }}</template>
        <template v-slot:[`item.period`]="{ item }">
          {{ toShortDateFormat(item.period.from) }} <br />
          {{ toShortDateFormat(item.period.to) }}
        </template>
        <template v-slot:[`item.options`]="{ item }">
          {{ getTimeFromPTH(item.options.checkIn) }}<br />
          {{ getTimeFromPTH(item.options.checkOut) }}<span v-if="item.options.nextDayCheckout">+1</span>
        </template>
      </restrictions-table>
      <chess
        v-if="$vuetify.display.mdAndUp"
        ref="chessRef"
        :period="checkInOutRestrictionsChessPeriod"
        :items="chessItems"
        :is-edit="
          isOperationEnabled(checkInOutRestrictions?.operations.edit) ||
          isOperationEnabled(checkInOutRestrictions?.operations.add)
        "
        :chess-dates-available-from="chessDatesAvailableFrom"
        :loading="isLoading"
        @select="handleChessSelect"
      />
    </div>

    <check-in-out-restriction-dialog
      v-model="isRestrictionDialogOpen"
      :selected-cells="selectedChessCells"
      :operation-delete="checkInOutRestrictions?.operations.remove"
      @close="chessRef?.unselectAllCells"
    />
    <remove-restriction-dialog
      v-model="isRemoveRestrictionDialogOpen"
      :number="selectedChessCells[0]?.rangeId"
      :action="removeRestrictionAction"
      :operation-delete="checkInOutRestrictions?.operations.remove"
    />

    <e-dialog v-model="isChessCalendarDialogOpen">
      <template #title>
        <div class="caption">Расчётные часы</div>
      </template>
      <chess-calendar
        ref="chessCalendarRef"
        :period="checkInOutRestrictionsChessPeriod"
        :table="chessItems[0].tables[0]"
        :cells="chessItems[0].tables[0].rows[0].cells"
        :chess-dates-available-from="chessDatesAvailableFrom"
        :is-edit="
          isOperationEnabled(checkInOutRestrictions?.operations.edit) ||
          isOperationEnabled(checkInOutRestrictions?.operations.add)
        "
        @select="handleChessSelect"
      />
    </e-dialog>
  </error-boundary>
</template>

<script setup lang="ts">
import dayjs from 'dayjs';
import { computed, nextTick, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useDisplay } from 'vuetify';
import type {
  ChessCalendarComponent,
  ChessComponent,
  ChessDateCellType,
  ChessItemType,
  ChessTableRowCellsType,
} from '@/core/components/interface/Chess/Chess.type';
import type { ArrayElementType } from '@/core/types/gengeric';
import type { RangeOfDateOnly } from '@/graphql/generated';
import ChessCalendar from '@/core/components/interface/Chess/ChessCalendar.vue';
import Chess from '@/core/components/interface/Chess/index.vue';
import EDialog from '@/core/components/modals/Dialog.vue';
import { useFetch } from '@/core/composables/useFetch';
import useToggle from '@/core/composables/useToggle';
import { getIsoDateWithUtcOffset, getTimeFromPTH, toShortDateFormat } from '@/core/utils/date';
import { isOperationEnabled } from '@/core/utils/permissions';
import ErrorBoundary from '@/parts/errors/components/ErrorBoundary.vue';
import { useAccommodationObjectsStore } from '@/parts/objects/store/accommodationObjects';
import CheckInOutRestrictionDialog from '@/parts/tariffs/components/PricesAndRestrictions/CheckInOut/CheckInOutRestrictionDialog.vue';
import CreateRestrictionButton from '@/parts/tariffs/components/PricesAndRestrictions/CreateRestrictionButton.vue';
import RemoveRestrictionDialog from '@/parts/tariffs/components/PricesAndRestrictions/RemoveRestrictionDialog.vue';
import RestrictionsTable from '@/parts/tariffs/components/PricesAndRestrictions/RestrictionsTable.vue';
import RestrictionsTableToggleButton from '@/parts/tariffs/components/PricesAndRestrictions/RestrictionsTableToggleButton.vue';
import { useRestrictionsStore } from '@/parts/tariffs/store/restrictions';

const $route = useRoute();
const display = useDisplay();
const [isRestrictionsTableOpen, setIsRestrictionsTableOpen] = useToggle();
const [isRemoveRestrictionDialogOpen, setIsRemoveRestrictionDialogOpen] = useToggle();
const [isRestrictionDialogOpen, setIsRestrictionDialogOpen] = useToggle();
const [isChessCalendarDialogOpen, setIsChessCalendarDialogOpen] = useToggle();
const {
  checkInOutRestrictions,
  checkInOutRestrictionsChessPeriod,
  getCheckInOutRestrictions,
  removeCheckInOutRestriction,
} = useRestrictionsStore();
const { timeZoneOffsetMin } = useAccommodationObjectsStore();

const chessRef = ref<ChessComponent>();
const chessCalendarRef = ref<ChessCalendarComponent>();
const selectedChessCells = ref<ChessDateCellType[]>([]);

const tariffScheduleCid = computed(() => String($route.params.t_id));

const chessItems = computed<ChessItemType[]>(() => {
  if (!checkInOutRestrictions.value) return [];

  return [
    {
      tables: [
        {
          rows: [
            {
              fixed: {
                text: '<div class="mb-md-1">Заезд</div><div>Выезд</div>',
                verticalAlign: 'end',
              },
              cells: checkInOutRestrictions.value.grid.reduce<ChessTableRowCellsType>((result, gridItem) => {
                return { ...result, ...getChessCellsFromGridItem(gridItem.period, gridItem.restrictionId) };
              }, {}),
            },
          ],
          fixedWidth: 80,
          rowCellWidth: 68,
          cellHeight: display.xs.value ? 52 : 66,
        },
      ],
    },
  ];
});

const chessDatesAvailableFrom = computed(() => {
  return getIsoDateWithUtcOffset(new Date(), timeZoneOffsetMin.value);
});

type CheckInOutRestriction = {
  period: RangeOfDateOnly;
};

const restrictionsTableHeaders = computed(() => [
  {
    key: 'number',
    title: '#',
    width: 48,
  },
  {
    key: 'period',
    title: 'Период',
    sortRaw: (v1: CheckInOutRestriction, v2: CheckInOutRestriction) =>
      `${toShortDateFormat(v2.period.from)} ${toShortDateFormat(v2.period.to)}` >
      `${toShortDateFormat(v1.period.from)} ${toShortDateFormat(v1.period.to)}`
        ? -1
        : 1,
  },
  {
    key: 'options',
    title: 'Условие',
    sortable: false,
  },
  {
    key: 'actions',
    sortable: false,
    width: 40,
  },
]);

const restrictionsTableItems = computed(() => {
  return checkInOutRestrictions.value?.table.map((item) => item.restriction) ?? [];
});

const handleChessSelect = async (cells: ChessDateCellType[]) => {
  selectedChessCells.value = cells;

  if (cells.length) {
    setTimeout(() => {
      setIsRestrictionDialogOpen(true);
    }, 10);
  }
};

const handleRestrictionShow = (item: ArrayElementType<typeof restrictionsTableItems.value>) => {
  if (display.mdAndUp.value) {
    chessRef.value?.scrollTo(item.period.from);
  } else {
    setIsChessCalendarDialogOpen(true);
    nextTick(() => {
      chessCalendarRef.value?.scrollTo(item.period.from);
    });
  }
};

const handleRestrictionEdit = (item: ArrayElementType<typeof restrictionsTableItems.value>) => {
  selectedChessCells.value = Object.values(getChessCellsFromGridItem(item.period, item.restrictionId));
  setIsRestrictionDialogOpen(true);
};

const handleRestrictionRemove = (item: ArrayElementType<typeof restrictionsTableItems.value>) => {
  selectedChessCells.value = Object.values(getChessCellsFromGridItem(item.period, item.restrictionId));
  setIsRemoveRestrictionDialogOpen(true);
};

const removeRestrictionAction = async () => {
  if (!selectedChessCells.value[0].id) return;

  await removeCheckInOutRestriction({
    restrictionId: Number(selectedChessCells.value[0].id),
    tariffScheduleCid: tariffScheduleCid.value,
  });
};

const getChessCellsFromGridItem = (period: RangeOfDateOnly, restrictionId?: number | null) => {
  const result: ChessTableRowCellsType = {};
  let options = checkInOutRestrictions.value?.defaults.defaultOptions;
  let rangeId;

  if (restrictionId) {
    const gridItemRestriction = checkInOutRestrictions.value?.table.find(
      (item) => item.restriction.restrictionId === restrictionId,
    );

    if (gridItemRestriction) {
      options = gridItemRestriction.restriction.options;
      rangeId = gridItemRestriction.restriction.number;
    }
  }

  let periodDate = period.from;

  while (periodDate <= period.to) {
    result[periodDate] = {
      date: periodDate,
      content: `<div class="mb-md-1">${getTimeFromPTH(options?.checkIn)}</div><div>${getTimeFromPTH(options?.checkOut)}${options?.nextDayCheckout ? '+1' : ''}</div>`,
      rangeId,
      id: restrictionId,
    };
    periodDate = dayjs(periodDate).add(1, 'day').format('YYYY-MM-DD');
  }

  return result;
};

const { isLoading } = useFetch(async () => {
  await getCheckInOutRestrictions(tariffScheduleCid.value);
});
</script>
